import type { ApolloError } from "@apollo/client/errors";
import { PubSubSharedActions } from "@selector/shared";
import { nanoid } from "nanoid";
import PubSub from "pubsub-js";
import { findIndex } from "ramda";
import { createContext, useContext } from "react";
import { createStore, type StateCreator, type StoreApi, useStore } from "zustand";
import { devtools } from "zustand/middleware";
import { immer } from "zustand/middleware/immer";
import { createWithEqualityFn } from "zustand/traditional";

import { query as Q } from "@/app-utils/graphql/query.ts";
import { PubSubActions } from "@/global/pubsub-actions.ts";
import type { IndexStatus } from "@/graphql.ts";
import type {
  BrowsingDirectoryFragment,
  BrowsingProjectFileFragment,
} from "@/modules/files/graphql/files-queries.gql.ts";
import { type Breadcrumb, generateBreadcrumbs } from "@/modules/files/utils/utils.ts";
import { type FileEvent, FileStatus } from "@/modules/notifications/utils/types.ts";

import {
  GetCollectorDirectoriesInfoDocument,
  type GetCollectorDirectoriesInfoQuery,
  type GetCollectorDirectoriesInfoQueryVariables,
} from "../graphql/files-queries.gql.ts";

interface FileBrowserState {
  files: BrowsingProjectFileFragment[] | null;
  loading: boolean;
  currentFolder?: BrowsingDirectoryFragment;
  error?: ApolloError;
  breadcrumbs?: Breadcrumb[];
  selectedFiles: BrowsingProjectFileFragment[];
}

interface FileBrowserActions {
  load: (folderId: number) => Promise<void>;
  refresh: (triggerFileScanner?: boolean) => Promise<void>;
  select: (files: BrowsingProjectFileFragment[]) => void;
  clear: () => void;
}

export type FileBrowserStore = FileBrowserState & FileBrowserActions;

const initialState: FileBrowserState = {
  files: null,
  currentFolder: undefined,
  loading: false,
  error: undefined,
  breadcrumbs: undefined,
  selectedFiles: [],
};

let lastRequestFolderId: number;
let lastRefreshRequestId: string;

const query = Q<GetCollectorDirectoriesInfoQuery, GetCollectorDirectoriesInfoQueryVariables>(
  GetCollectorDirectoriesInfoDocument,
);

const fileStoreStateCreator: StateCreator<FileBrowserStore, [], [["zustand/immer", never]]> = immer<FileBrowserStore>(
  (set, get) => {
    PubSub.subscribe(PubSubSharedActions.LOGOUT, () => {
      set(initialState);
    });

    PubSub.subscribe(PubSubActions.FILE, (_, data: FileEvent) => {
      // FIXME, You will receive notification as much time as there is a store instance,
      // So notification should be managed at a higher level.
      if (import.meta.env.DEV) console.log("File Browser Store WebSocket update", data);

      const { status, fileId } = data;

      // IndexStatus and FileStatus are partially compatible
      switch (status) {
        case FileStatus.Indexing:
        case FileStatus.Exists:
        case FileStatus.Failed:
          set((state) => {
            // Update FileBrowser Files
            if (state.files) {
              const idx = findIndex((file: BrowsingProjectFileFragment) => file.id === fileId)(state.files);
              if (idx !== -1) {
                state.files[idx].indexingStatus = status as unknown as IndexStatus;
              }
            }
            // Update state.selectedFiles too
            if (state.selectedFiles) {
              const idx = findIndex((file: BrowsingProjectFileFragment) => file.id === fileId)(state.selectedFiles);
              if (idx !== -1) {
                state.selectedFiles[idx].indexingStatus = status as unknown as IndexStatus;
              }
            }
          });
      }
    });

    return {
      ...initialState,
      load: async (folderId) => {
        lastRequestFolderId = folderId;
        // TODO We should clean breadcrumb if folderId is not in the list of breadcrumbs
        set({ loading: true, files: null, error: undefined });
        const { data, error } = await query({ folderId, refresh: null });
        const files = data?.collectorDirectoriesInfo;
        if (error) {
          set({ ...initialState, error, loading: false });
        } else if (files && files.parentFolder?.id === lastRequestFolderId) {
          set({
            currentFolder: files.parentFolder satisfies BrowsingDirectoryFragment,
            files: files.children satisfies BrowsingProjectFileFragment[],
            loading: false,
            breadcrumbs: generateBreadcrumbs(files.parentFolder),
          });
        }
      },
      refresh: async (triggerFileScanner = false) => {
        const currentFolder = get().currentFolder;
        // You may call refresh while loading something already
        if (currentFolder?.id != undefined && currentFolder.id === lastRequestFolderId) {
          const reqUid = nanoid();
          lastRefreshRequestId = reqUid;
          set({ loading: true, error: undefined }); // Keep the previous files as we are updating the current folder
          const { data, error } = await query({ folderId: currentFolder.id, refresh: triggerFileScanner });
          if (reqUid === lastRefreshRequestId) {
            const browsingInfo = data?.collectorDirectoriesInfo;
            if (error) {
              set({ ...initialState, error });
            } else if (browsingInfo) {
              set({ files: browsingInfo.children, loading: false });
            }
          } else {
            set({ loading: false }); // because we load but don't update, it's not the last query.
          }
        }
      },
      select: (selectedFiles) => {
        set({ selectedFiles });
      },
      clear: () => {
        // Just keep the currentFolder so that when you come back, it loads the previous folder
        const currentFolder = get().currentFolder;
        set({ ...initialState, currentFolder });
      },
    };
  },
);

// We split the stores because we can have a FilePicker on top of the FileBrowser.
// In addition, we want to keep the `currentFolder` separated anyway.
export const useFileBrowserStore = createWithEqualityFn<
  FileBrowserStore,
  [["zustand/devtools", never], ["zustand/immer", never]]
>(devtools(fileStoreStateCreator, { enabled: import.meta.env.DEV, name: "File Browser Store" }));

export const useFilePickerStore = createWithEqualityFn<
  FileBrowserStore,
  [["zustand/devtools", never], ["zustand/immer", never]]
>(devtools(fileStoreStateCreator, { enabled: import.meta.env.DEV, name: "File Picker Store" }));

export const createFileBrowserStore = () =>
  createStore<FileBrowserStore, [["zustand/immer", never]]>(fileStoreStateCreator);
export const FileBrowserContext = createContext<StoreApi<FileBrowserStore>>({} as StoreApi<FileBrowserStore>);
export function _useFileBrowserStore<T>(selector: (state: FileBrowserStore) => T): T {
  const store = useContext(FileBrowserContext);

  if (!store) {
    throw new Error("Missing Store Provider");
  }

  return useStore(store, selector);
}
